import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/utils/cn';
import { Button } from '@/components/ui/Button';
import { Icon } from '@/components/ui/Icon';
import { ICONS } from '@/constants/icons';
import { useAI } from '@/hooks/useAI';
import { useAuth } from '@/hooks/useAuth';
import { useAIBrandProfiles } from '@/hooks/useAIBrandProfiles';
import { AIBriefingModal } from '@/components/composer/AIBriefingModal';

/**
 * AISection Component
 *
 * Unified AI assistant with Generate and Enhance tabs
 * - Generate: Create new captions from scratch
 * - Enhance: Improve existing caption
 * - Brief Mägic Ed: Comprehensive AI briefing modal
 */

type AITab = 'generate' | 'enhance';

interface AISectionProps {
  caption: string;
  onCaptionChange: (caption: string) => void;
  selectedChannels: number[];
  disabled: boolean;
}

export const AISection = ({
  caption,
  onCaptionChange,
  selectedChannels,
  disabled,
}: AISectionProps) => {
  const { t } = useTranslation(['ai', 'common']);
  const [activeTab, setActiveTab] = useState<AITab>('generate');
  const [isBriefingModalOpen, setIsBriefingModalOpen] = useState(false);

  // Get user info for personalized greeting
  const { user } = useAuth();

  // Team's active default brand profile — auto-applied silently (no picker in this surface)
  const { defaultProfile } = useAIBrandProfiles();

  // Generate options
  const [tone, setTone] = useState<'professional' | 'casual' | 'funny'>('professional');
  const [length, setLength] = useState<'short' | 'medium' | 'long'>('medium');

  // Enhance options
  const [enhanceOption, setEnhanceOption] = useState<
    'grammar' | 'emojis' | 'concise' | 'detailed'
  >('grammar');

  const hasCaption = caption.trim().length > 0;

  // AI hook
  const { isGenerating, isImproving, generateCaption, improveContent, error, clearError } = useAI();

  const handleGenerate = async () => {
    clearError();

    const result = await generateCaption({
      topic: caption || undefined,
      tone: tone,
      length: length,
      channelIds: selectedChannels,
      brandProfileId: defaultProfile?.id,
    });

    if (result) {
      onCaptionChange(result.content);
    }
  };

  const handleEnhance = async () => {
    if (!caption.trim()) {
      return;
    }

    clearError();

    const result = await improveContent({
      content: caption,
      instruction: enhanceOption,
      brandProfileId: defaultProfile?.id,
    });

    if (result) {
      onCaptionChange(result.content);
    }
  };

  return (
    <>
      <div className="border border-primary-200 rounded-lg overflow-hidden bg-gradient-to-r from-primary-50 to-off-white">
        {/* Header with Icon and Title */}
        <div className="flex items-center justify-between px-4 pt-4 pb-2">
          <div className="flex items-center gap-2">
            <Icon name={ICONS.SPARKLES} size="sm" color="text-primary-700" />
            <h3 className="text-sm font-semibold text-gray-900">{t('ai_assistant')}</h3>
          </div>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setIsBriefingModalOpen(true)}
            disabled={disabled}
            className="text-xs border-2 border-transparent focus-visible:border-primary-200"
          >
            <Icon name={ICONS.SPARKLES} size="xs" className="mr-1.5" />
            {t('brief_magic_ed')}
          </Button>
        </div>

      {/* Error Message */}
      {error && (
        <div className="mx-4 mb-2 px-3 py-2 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-xs text-red-700">{error}</p>
        </div>
      )}

      {/* Tabs */}
      <div className="px-4">
        <div className="flex border-b border-primary-200">
          <button
            type="button"
            onClick={() => setActiveTab('generate')}
            className={cn(
              'px-4 py-2.5 text-sm font-medium transition-all duration-200 relative rounded-t-lg',
              'border-2 border-transparent focus:outline-none focus-visible:border-primary-800',
              activeTab === 'generate'
                ? 'text-primary-800 bg-white/50'
                : 'text-gray-500 hover:text-primary-700 hover:bg-white/30'
            )}
          >
            <span className="flex items-center gap-1.5">
              <Icon name={ICONS.SPARKLES} size="xs" />
              {t('ai_generate')}
            </span>
            <span className={cn(
              'absolute bottom-0 left-0 right-0 h-0.5 transition-all duration-200',
              activeTab === 'generate' ? 'bg-secondary-500' : 'bg-transparent'
            )} />
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('enhance')}
            className={cn(
              'px-4 py-2.5 text-sm font-medium transition-all duration-200 relative rounded-t-lg',
              'border-2 border-transparent focus:outline-none focus-visible:border-primary-800',
              activeTab === 'enhance'
                ? 'text-primary-800 bg-white/50'
                : 'text-gray-500 hover:text-primary-700 hover:bg-white/30'
            )}
          >
            <span className="flex items-center gap-1.5">
              <Icon name={ICONS.MAGIC_WAND} size="xs" />
              {t('ai_enhance')}
            </span>
            <span className={cn(
              'absolute bottom-0 left-0 right-0 h-0.5 transition-all duration-200',
              activeTab === 'enhance' ? 'bg-secondary-500' : 'bg-transparent'
            )} />
          </button>
        </div>
      </div>

      {/* Tab Content */}
      <div className="p-4">
        {activeTab === 'generate' && (
          <GenerateTabContent
            tone={tone}
            setTone={setTone}
            length={length}
            setLength={setLength}
            onGenerate={handleGenerate}
            disabled={disabled}
            isGenerating={isGenerating}
          />
        )}

        {activeTab === 'enhance' && (
          <EnhanceTabContent
            hasCaption={hasCaption}
            enhanceOption={enhanceOption}
            setEnhanceOption={setEnhanceOption}
            onEnhance={handleEnhance}
            disabled={disabled}
            isImproving={isImproving}
          />
        )}
      </div>
    </div>

    {/* AI Briefing Modal */}
    <AIBriefingModal
      isOpen={isBriefingModalOpen}
      onClose={() => setIsBriefingModalOpen(false)}
      onInsertCaption={onCaptionChange}
      userName={user?.fullName || user?.email?.split('@')[0] || 'there'}
    />
  </>
  );
};

/**
 * Generate Tab Content
 */
interface GenerateTabContentProps {
  tone: 'professional' | 'casual' | 'funny';
  setTone: (tone: 'professional' | 'casual' | 'funny') => void;
  length: 'short' | 'medium' | 'long';
  setLength: (length: 'short' | 'medium' | 'long') => void;
  onGenerate: () => void;
  disabled: boolean;
  isGenerating: boolean;
}

const GenerateTabContent = ({
  tone,
  setTone,
  length,
  setLength,
  onGenerate,
  disabled,
  isGenerating,
}: GenerateTabContentProps) => {
  const { t } = useTranslation(['ai', 'common']);

  return (
    <div className="space-y-4">
      <p className="text-xs text-gray-600">
        {t('ai_generate_description')}
      </p>

      {/* Tone Selection */}
      <div>
        <label className="block text-xs font-medium text-gray-700 mb-2">
          {t('ai_tone')}
        </label>
        <div className="flex flex-wrap gap-2">
          {(['professional', 'casual', 'funny'] as const).map((option) => (
            <button
              key={option}
              type="button"
              onClick={() => setTone(option)}
              className={cn(
                'px-3 py-1.5 text-xs font-medium rounded-full transition-colors',
                'border-2 border-transparent focus:outline-none focus-visible:border-primary-800',
                tone === option
                  ? 'border-secondary-500 bg-secondary-500 text-secondary-500-contrast focus-visible:border-primary-200'
                  : 'border-gray-300 bg-white text-gray-700 hover:border-primary-500 focus-visible:border-primary-800'
              )}
            >
              {t(`tone_${option}`)}
            </button>
          ))}
        </div>
      </div>

      {/* Length Selection */}
      <div>
        <label className="block text-xs font-medium text-gray-700 mb-2">
          {t('ai_length')}
        </label>
        <div className="flex flex-wrap gap-2">
          {(['short', 'medium', 'long'] as const).map((option) => (
            <button
              key={option}
              type="button"
              onClick={() => setLength(option)}
              className={cn(
                'px-3 py-1.5 text-xs font-medium rounded-full transition-colors',
                'border-2 border-transparent focus:outline-none focus-visible:border-primary-800',
                length === option
                  ? 'border-secondary-500 bg-secondary-500 text-secondary-500-contrast focus-visible:border-primary-200'
                  : 'border-gray-300 bg-white text-gray-700 hover:border-primary-500 focus-visible:border-primary-800'
              )}
            >
              {t(`length_${option}`)}
            </button>
          ))}
        </div>
      </div>

      {/* Generate Button */}
      <div className="flex justify-end pt-2">
        <Button
          variant="secondary"
          size="sm"
          onClick={onGenerate}
          disabled={disabled || isGenerating}
          loading={isGenerating}
          className="border-2 border-transparent focus-visible:border-primary-200"
        >
          {!isGenerating && <Icon name={ICONS.SPARKLES} size="xs" className="mr-1.5" />}
          {isGenerating ? t('generating') : t('generate_caption')}
        </Button>
      </div>
    </div>
  );
};

/**
 * Enhance Tab Content
 */
interface EnhanceTabContentProps {
  hasCaption: boolean;
  enhanceOption: 'grammar' | 'emojis' | 'concise' | 'detailed';
  setEnhanceOption: (option: 'grammar' | 'emojis' | 'concise' | 'detailed') => void;
  onEnhance: () => void;
  disabled: boolean;
  isImproving: boolean;
}

const EnhanceTabContent = ({
  hasCaption,
  enhanceOption,
  setEnhanceOption,
  onEnhance,
  disabled,
  isImproving,
}: EnhanceTabContentProps) => {
  const { t } = useTranslation(['ai', 'common']);

  const enhanceOptions = [
    { id: 'grammar', label: t('ai_fix_grammar'), icon: ICONS.CHECK },
    { id: 'emojis', label: t('ai_add_emojis'), icon: ICONS.EMO_SMILE },
    { id: 'concise', label: t('ai_make_concise'), icon: ICONS.CROP },
    { id: 'detailed', label: t('ai_make_detailed'), icon: ICONS.DOCUMENT },
  ] as const;

  if (!hasCaption) {
    return (
      <div className="text-center py-6">
        <Icon
          name={ICONS.PEN}
          size="lg"
          className="mx-auto mb-2 text-gray-400"
        />
        <p className="text-sm text-gray-500">
          {t('ai_write_first')}
        </p>
        <p className="text-xs text-gray-400 mt-1">
          {t('ai_enhance_hint')}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <p className="text-xs text-gray-600">
        {t('ai_enhance_description')}
      </p>

      {/* Enhancement Options */}
      <div className="grid grid-cols-2 gap-2">
        {enhanceOptions.map((option) => (
          <button
            key={option.id}
            type="button"
            onClick={() => setEnhanceOption(option.id)}
            className={cn(
              'flex items-center gap-2 px-3 py-2.5 rounded-lg text-xs font-medium transition-colors',
              'border-2 border-transparent focus:outline-none focus-visible:border-primary-800',
              enhanceOption === option.id
                ? 'border-secondary-500 bg-secondary-500 text-secondary-500-contrast focus-visible:border-primary-200'
                : 'border-gray-300 bg-white text-gray-700 hover:border-primary-500 focus-visible:border-primary-800'
            )}
          >
            <Icon
              name={option.icon}
              size="xs"
              className={enhanceOption === option.id ? 'text-secondary-500-contrast' : 'text-gray-500'}
            />
            {option.label}
          </button>
        ))}
      </div>

      {/* Enhance Button */}
      <div className="flex justify-end pt-2">
        <Button
          variant="secondary"
          size="sm"
          onClick={onEnhance}
          disabled={disabled || isImproving}
          loading={isImproving}
          className="border-2 border-transparent focus-visible:border-primary-200"
        >
          {!isImproving && <Icon name={ICONS.MAGIC_WAND} size="xs" className="mr-1.5" />}
          {isImproving ? t('enhancing') : t('enhance_caption')}
        </Button>
      </div>
    </div>
  );
};
